import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { bool, func, node, object, string } from 'prop-types';
import {
  translate,
  LocaleProviderCtx,
} from '@airasia-common/libraries/i18n/LocaleProvider';
import {
  transformGuestsToOccupancy,
  parseOccupancies,
} from '@airasia-common/libraries/utilities/OccupancyHelper';

import loadable from '@loadable/component';
import SummaryHeader from '@airasia-common/react-dom/components/SummaryHeader';

import {
  HotelSearchWidgetProvider,
  EnvironmentTypes,
} from '@airasia-hotels/hotel-search-widget-web';
import { store } from '@airasia/ui-utils';
import loadableVisibility from 'react-loadable-visibility/loadable-components';
// import { getRoomOccupancy } from '@airasia-hotels/hotel-search-widget-web/utils/AaSearchUrl';
import config from '@airasia-common/libraries/config';
import { useHistory } from 'react-router-dom';
import Store from '@airasia/phoenix-widgets/widgets/Store';

import PhoenixTheme from '@airasia/phoenix-components/components/Theme';
import { Themes } from '@airasia/phoenix-components/components/Theme/constant';
import { formatAPIDayParam } from '@airasia-common/libraries/datetime';
import styled from 'styled-components';
import { hotelFilterDefault } from '@airasia-hotels/core/ParamFilter';
import { getRoomOccupancy, getChildAges } from '../../utils/SearchWidgetUtil';
import { navigateToSearch } from '../../utils/navigation';
import { useWindowSize } from '../../utils';
import {
  getTextsFromFilter,
  StyledHeaderSubTitle,
} from '../../utils/HeaderTitleCreator';
import { deviceInfo } from '../../utils/DeviceAgent';
const HotelSearchWidget = loadableVisibility(() =>
  import(
    /* webpackChunkName:"HotelSearchWidget" */ '@airasia/phoenix-widgets/widgets/HotelSearchWidget'
  )
);
const EditableMobileNavHeader = loadableVisibility(() =>
  import(
    /* webpackChunkName:"EditableMobileNavHeader" */ '@airasia-common/react-dom/components/EditableMobileNavHeader'
  )
);

const FakeSearchWidget = loadableVisibility(() =>
  import(
    /* webpackChunkName:"FakeSearchWidget" */ '@airasia-common/react-dom/components/FakeSearchWidget'
  )
);

const Container = loadableVisibility(() =>
  import(
    /* webpackChunkName:"Container" */ '@airasia-common/react-dom/components/Container/Container'
  )
);

const Wrapper = loadableVisibility(() =>
  import(
    /* webpackChunkName:"Wrapper" */ '@airasia-common/react-dom/components/Wrapper/Wrapper'
  )
);

const MobileNavHeader = loadableVisibility(() =>
  import(
    /* webpackChunkName:"MobileNavHeader" */ '@airasia-common/react-dom/components/Header/MobileNavHeader'
  )
);

const HotelSearchWidgetWrapper = styled(Wrapper)`
  top: 0;
  padding-top: 16px;
  height: min-content;
  background-color: ${({ theme }) => theme.colors.greyLighter};
  padding-bottom: 16px;
  #HSW-calendar {
    background-color: ${({ theme }) => theme.colors.primaryWhite};
  }
`;

const SEPARATORS = {
  CHILD: ',',
  ADULT: ',',
  ROOM: '_',
  SEARCH_QUERY: '?',
  QUERY_PARAM: '&',
  KEY_VALUE: '=',
};

const DEFAULT_AGES = { CHILD: 17, INFANT: 1 };

const HotelsSummaryHeader = props => {
  const {
    filter,
    showSearchHeader,
    enableMobileSearchWidget,
    enableDesktopSearchWidget,
    trackHotelEditSearchClicked,
    rightItem,
    handleMobileBackButton,
    updateFilter,
    destination,
    isCalendarOpenedByDeepLink,
    trackHotelSearchWidget,
    isApplyHotelList,
    uhswData,
    setUserLocationSelected,
    locationFilterSelected,
    suggestion,
    move,
    theme,
  } = props;
  const polyglot = React.useContext(LocaleProviderCtx);
  const summaryHeaderSubtitle = useMemo(
    () => <StyledHeaderSubTitle filter={filter} />,
    // Need to re-render when locale is changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter, polyglot]
  );
  const history = useHistory();
  let { location } = history;
  const { isMobileView } = useWindowSize();
  const showMobileSearchWidget = isMobileView && enableMobileSearchWidget;
  const showDesktopSearchWidget = !isMobileView && enableDesktopSearchWidget;
  const [showSearchWidget, setShowSearchWidget] = useState(
    showDesktopSearchWidget
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const clearFocus = () => {
    try {
      const query = new URLSearchParams(location.search);
      if (query.get('focusId')) {
        query.delete('focusId');
        location.search = query.toString();
        delete filter.focusId;
        history.push(location);
      }
    } catch (ex) {
      console.error(ex);
    }
  };
  const onClickEditSearch = useCallback(() => {
    if (!showMobileSearchWidget) {
      navigateToSearch(filter);
    } else {
      setShowSearchWidget(true);

      if (deviceInfo.os === 'Android') {
        history.push(location, '#o');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, showMobileSearchWidget]);

  const hideSearchWidget = useCallback(() => {
    const body = document.getElementsByTagName('body');
    if (body) {
      body[0].removeAttribute('style');
    }
    // Clear history state
    window.history.replaceState({}, document.title);

    setShowSearchWidget(false);
  }, []);

  const searchHandler = useCallback(
    listingProps => {
      const { adults, children } = listingProps;
      clearFocus();
      const occupancy = transformGuestsToOccupancy(adults, children);
      const updatedFilter = {
        ...filter,
        ...listingProps,
        occupancy,
        guests: parseOccupancies(occupancy),
      };

      updateFilter(updatedFilter);

      if (isMobileView) {
        hideSearchWidget();
      }
    },
    [clearFocus, filter, updateFilter, isMobileView, hideSearchWidget]
  );

  const buildParams = searchParams => {
    const { checkin, checkout, destination, rooms, childsAge } = searchParams;
    const children = rooms
      .map((e, i) => formatChildComponent(e, i, childsAge))
      .join(SEPARATORS.CHILD);
    const params = {
      checkin: formatAPIDayParam(checkin),
      checkout: formatAPIDayParam(checkout),
      roomsCount: rooms.length,
      adults: rooms.map(room => room.adult).join(SEPARATORS.ADULT),
      children,
    };

    if (destination.id) {
      params.locationId = destination.id;
      params.location = destination.name;
      params.isClickedDestinationItem = destination.isClickedDestinationItem;
    }

    params.aaId = '';
    if (destination.aa_id) {
      params.aaId = destination.aa_id;
    }

    return params;
  };

  const searchHandlerForNewHSW = useCallback(
    listingProps => {
      const params = buildParams(listingProps);
      const { adults, children } = params;
      clearFocus();
      const occupancy = transformGuestsToOccupancy(adults, children);
      const updatedFilter = {
        ...filter,
        ...params,
        occupancy,
        guests: parseOccupancies(occupancy),
        needToFetchLocationInfo: true,
        sortBy: 'recommended',
        guestRating: '',
        amenities: [],
        amenity: [],
        isResetAmenitiesFilter: Object.prototype.hasOwnProperty.call(
          filter,
          'isResetAmenitiesFilter'
        )
          ? !filter.isResetAmenitiesFilter
          : true,
      };

      updateFilter(updatedFilter);

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      if (isMobileView) {
        hideSearchWidget();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter, isMobileView]
  );

  const { date, guests, rooms, checkin, checkout } = getTextsFromFilter(filter);

  const formatChildComponent = (room, index, childsAge) => {
    let childsAgePerRoom =
      childsAge && childsAge[index] ? childsAge[index] : [];
    const { child: childCount, infant: infantCount } = room;
    const roomNumber = index + 1;
    let children = Array(childCount).fill(
      `${roomNumber}${SEPARATORS.ROOM}${DEFAULT_AGES.CHILD}`
    );
    children = children.map(
      (e, i) =>
        `${roomNumber}${SEPARATORS.ROOM}${
          childsAgePerRoom && childsAgePerRoom[i]
            ? childsAgePerRoom[i].value
            : DEFAULT_AGES.CHILD
        }`
    );
    const infants = Array(infantCount).fill(
      `${roomNumber}${SEPARATORS.ROOM}${DEFAULT_AGES.INFANT}`
    );

    return [...children, ...infants].join(SEPARATORS.CHILD);
  };

  useEffect(() => {
    if (deviceInfo.os === 'Android') {
      window.onpopstate = () => {
        if (showSearchWidget) {
          hideSearchWidget();
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSearchWidget]);

  const hotelsSearchWidget = useMemo(() => {
    const { adults = '', children = '' } = filter;
    const roomOccupancy = getRoomOccupancy(adults, children);
    const dest = destination?.name || filter.location;
    const childsAge = getChildAges(children);

    const preFill = {
      destination: destination ?? filter.locationId,
      destinationName: dest,
      roomOccupancy: roomOccupancy,
      checkin: filter.checkin,
      checkout: filter.checkout,
      childsAge: !childsAge || childsAge.length === 0 ? [[]] : childsAge,
    };
    const environment =
      EnvironmentTypes[config.REACT_APP_ENVIRONMENT] ||
      EnvironmentTypes.DEVELOPMENT;
    const endpointConfig =
      uhswData && uhswData['ENDPOINT_CONFIG']
        ? uhswData['ENDPOINT_CONFIG'][0].data
        : [];

    const landmark =
      suggestion?.nearby_poi &&
      suggestion?.nearby_poi.find(poi => poi.id === filter.locationId)?.name;
    const neigborhood =
      suggestion?.neighborhood &&
      suggestion?.neighborhood.find(
        neighbor => neighbor.id === filter.locationId
      )?.name;

    return (
      <>
        <HotelSearchWidgetWrapper>
          <Container>
            <PhoenixTheme theme={theme}>
              <Store>
                <HotelSearchWidget
                  data={uhswData}
                  theme={move ? 'AIRASIA_MOVE' : Themes}
                  prefill={preFill}
                  endpointConfig={endpointConfig}
                  maxRangeDays={30}
                  maxDays={365} // Please keep featureflag in future reference.
                  isOnlyApplyForHotel
                  onSubmitHandler={searchHandlerForNewHSW}
                  fireGtm={trackHotelSearchWidget}
                  viewport={isMobileView ? 'mobile' : 'desktop'}
                  closeSearchWidget={hideSearchWidget}
                  isApplyHotelList={isApplyHotelList}
                  environment={environment}
                  setUserLocationSelected={setUserLocationSelected}
                  hotelSuggestion={{
                    ...suggestion,
                    name: landmark || neigborhood || suggestion?.name,
                    id: filter?.locationId,
                  }}
                  locale={filter?.languageId || hotelFilterDefault?.languageId}
                  move={move}
                  geoId={filter?.countryCode}
                />
              </Store>
            </PhoenixTheme>
          </Container>
        </HotelSearchWidgetWrapper>
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, destination, isMobileView, uhswData]);

  if (showSearchHeader) {
    const dest = destination?.name || filter.location;

    return (
      <>
        {uhswData && showSearchWidget ? (
          hotelsSearchWidget
        ) : (
          <FakeSearchWidget
            onClickEditSearch={onClickEditSearch}
            dest={dest}
            fromDate={checkin}
            toDate={checkout}
            guests={`${guests}    ${rooms}`}
          />
        )}
        <EditableMobileNavHeader
          date={date}
          dest={dest}
          guest={guests}
          room={rooms}
          onClickBack={handleMobileBackButton}
          onClickEditSearch={onClickEditSearch}
          rightItem={rightItem}
          showDateInSubtitle
        />
      </>
    );
  }

  return (
    <>
      <MobileNavHeader
        title={filter.location}
        subTitle={summaryHeaderSubtitle}
        onClick={handleMobileBackButton}
        rightItem={rightItem}
      />
      <SummaryHeader
        title={translate('hotel.hotelList.title', {
          destination: filter.location,
        })}
        subTitle={summaryHeaderSubtitle}
        onEditSearch={trackHotelEditSearchClicked}
      />
    </>
  );
};
HotelsSummaryHeader.propTypes = {
  showSearchHeader: bool,
  enableMobileSearchWidget: bool,
  enableDesktopSearchWidget: bool,
  locationFilterSelected: bool,
  filter: object,
  destination: object,
  trackHotelEditSearchClicked: func,
  rightItem: node,
  handleMobileBackButton: func,
  updateFilter: func,
  isCalendarOpenedByDeepLink: bool,
  trackHotelSearchWidget: func,
  isApplyHotelList: bool,
  uhswData: object,
  isHotelDetail: bool,
  setUserLocationSelected: string,
  suggestion: object,
};

export default HotelsSummaryHeader;
